import { Component, Host, Listen, Prop, State, h } from "@stencil/core";

import { SiteFeatureCollection, SiteProperties } from "@models/site";
import { fetchSites } from "@utils/data";
@Component({
	tag: "sitenna-site-navigator",
	styleUrl: "sitenna-site-navigator.scss",
	shadow: true,
})
export class SitennaSiteNavigator {
	/**
	 * The Sitenna API Key, this is required to use the component.
	 * This availble through the Sitenna Platform.
	 * If you have issues finding it, please contact support@sitenna.com
	 * @required
	 */
	@Prop({ attribute: "api-key" }) sitennaApiKey!: string;

	/**
	 * The Sitenna endpoint to use.
	 * This is available through the Sitenna Platform.
	 * If you have issues finding it, please contact support@sitenna.com
	 * @required
	 */
	@Prop({ attribute: "api-endpoint" }) apiEndpoint = "api.sitenna.com";

	/**
	 * The Sitenna app url to use.
	 * This availble through the Sitenna Platform.
	 * If you have issues finding it, please contact support@sitenna.com
	 * @optional
	 */
	@Prop({ attribute: "app-url" }) appUrl = "app.sitenna.com";

	/**
	 * Latitude you want the map to use. Default: 39.8
	 * @optional
	 * @number
	 */
	@Prop() latitude = 39.8;

	/**
	 * Longitude you want the map to use. Default: -98.5
	 * @optional
	 * @number
	 */
	@Prop() longitude = -98.5;

	/**
	 * Zoom you want the map to use. Default: 4.5
	 * @optional
	 * @number
	 */
	@Prop() zoom = 4.5;

	/**
	 * Max zoom level on the map. Default: 20
	 * @optional
	 * @number
	 */
	@Prop() maxZoom = 20;

	/**
	 * Min zoom level on the map. Default: 3
	 * @optional
	 * @number
	 */
	@Prop() minZoom = 3;

	// state
	@State() sites: SiteFeatureCollection;
	@State() showContactForm = false;
	@State() activeSite: SiteProperties;
	@State() isSideMenuOpen = false;

	private organizationID: string;

	constructor() {
		if (!this.sitennaApiKey || !this.apiEndpoint) return;

		// fetch sites from sitenna endpoint
		fetchSites(this.sitennaApiKey, this.apiEndpoint)
			.then((response) => {
				this.sites = response.sites;
				this.organizationID = response.organizationID;
			})
			.catch((error) => {
				console.error(error);
			});
	}

	private apiKeyError() {
		return (
			<p>
				An "api-key" is required to use this component. This availble through
				the Sitenna Platform. If you have issues finding it, please contact
				support@sitenna.com.
			</p>
		);
	}

	private siteClickCallback = (site: SiteProperties) => {
		this.activeSite = site;
		this.isSideMenuOpen = true;
	};

	private closeModalCallback = () => {
		this.showContactForm = false;
		this.activeSite = undefined;
	};

	@Listen("closeSidebarEvent")
	closeSidebarEvent() {
		this.isSideMenuOpen = false;
	}

	@Listen("showContactFormEvent")
	showContactFormEvent() {
		this.showContactForm = true;
		this.isSideMenuOpen = false;
	}

	render() {
		if (!this.sitennaApiKey) return this.apiKeyError();
		return (
			<Host>
				<side-panel
					organizationID={this.organizationID}
					sitennaApiKey={this.sitennaApiKey}
					apiEndpoint={this.apiEndpoint}
					site={this.activeSite}
					appUrl={this.appUrl}
					class={{ hidden: !this.isSideMenuOpen }}
				/>
				<mapbox-map
					sites={this.sites}
					zoom={this.zoom}
					latitude={this.latitude}
					longitude={this.longitude}
					siteClickCallback={this.siteClickCallback}
					isSideMenuOpen={this.isSideMenuOpen}
				/>
				{this.showContactForm && (
					<contact-form
						sitennaApiKey={this.sitennaApiKey}
						apiEndpoint={this.apiEndpoint}
						site={this.activeSite}
						closeCallback={this.closeModalCallback}
					/>
				)}
				<sitenna-logo id="logo" />
			</Host>
		);
	}
}
